import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { AvailableLanguages } from '@repo/toolkit/language-types'
import { StringFormatter } from '@repo/toolkit/string'
import { toFullName } from '@repo/toolkit/to-full-name'
import Badge from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'
import { TableDate } from '@repo/ui/data-table-cells'
import { endOfDay, isAfter, isBefore, startOfDay } from 'date-fns'
import { t, TFunction } from 'i18next'

import ArrowRightOneIcon from '../../../../../packages/ui/icons/arrow-right-01'
import CountryFlag from '../../components/elements/country-flag'
import PeriodBadge from '../../components/elements/period-badge'
import {
  QualificationBadge,
  QualificationTags
} from '../../components/elements/qualification-badge'
import { StaffAccountStatusBadge } from '../../components/pages/staff/staff-account-status'
import { genderEnumMapping, toGenderEnumValue } from '../../lib/types/enums/common/gender.enum'
import { LanguageEnum, languageEnumMapping } from '../../lib/types/enums/languages/language.enum'
import { LanguageLevelEnum } from '../../lib/types/enums/languages/language-level.enum'
import { missionStatusEnumMapping } from '../../lib/types/enums/mission/mission-status.enum'
import { academicZoneEnumMapping } from '../../lib/types/enums/staff/academic-zone.enum'
import { staffPositionEnumMapping } from '../../lib/types/enums/staff/staff-position.enum'
import { PeriodTypes } from '../../lib/types/models/period/period'
import { AdminStaffInput } from '../../lib/types/models/staff/admin-staff-input'
import { Staff } from '../../lib/types/models/staff/staff'
import { StaffCommentInput } from '../../lib/types/models/staff-comment/staff-comment-input'
import { StaffRequest } from '../../lib/types/models/staff-request/staff-request'
import { getTranslatedName } from '../../lib/utils/functions/translation.functions'

export const mapStaffToInput = (staff: Staff): AdminStaffInput => {
  const { id: _id, ...rest } = staff

  const comments: StaffCommentInput[] = rest.comments?.map(
    ({ createdAt, id, comment, displayForDirector, commentedBy, displayInPlanning, subject }) => ({
      id,
      date: createdAt,
      comment,
      displayForDirector,
      displayInPlanning,
      subject,
      commentedByName: toFullName(commentedBy.firstName, commentedBy.lastName)
    })
  )

  return {
    ...rest,
    availabilities: rest.availabilities?.map(({ periods, startDate, endDate }) => ({
      startDate,
      endDate,
      periods
    })),
    hobbyIds: rest.hobbies?.map(({ id }) => id),
    canModify: rest.canModify,
    email: rest.user?.email,
    howDidYouHearAboutUsOptionId: rest.howDidYouHearAboutUsOption?.id,
    diplomas: rest.diplomas?.map(({ staff: _staff, staffId: _staffId, ...rest }) => ({ ...rest })),
    countriesWishListIds: rest.countriesWishList?.map(({ id }) => id),
    formulasWishListIds: rest.formulasWishList?.map(({ id }) => id),
    languages: Object.values(LanguageEnum).map((language) => {
      const practicedLanguage = rest.languages?.find(({ language: lang }) => lang === language)
      return { language, level: practicedLanguage?.level || LanguageLevelEnum.NOT_PRACTICED }
    }),
    comments,
    applicationId: rest.application?.id
  }
}

type StaffVersion = 'FULL' | 'PARTIAL' | 'LIGHT'

export const staffColumns: (
  t: TFunction<AvailableLanguages, undefined>
) => (Column<Staff> & { version: StaffVersion[] })[] = () => {
  const languageMapping = languageEnumMapping()
  const genderMapping = genderEnumMapping()
  return [
    {
      version: ['FULL'],
      enableSorting: true,
      accessorKey: 'id',
      header: 'N°'
    },
    {
      version: ['FULL'],
      enableSorting: true,
      accessorKey: 'matricule',
      header: 'Matricule',
      cell: ({ row }) => <Badge variant="primary" text={`#${row.original.matricule}`} />
    },
    {
      version: ['FULL'],
      accessorKey: 'picturePath',
      header: 'Photo',
      cell: ({ row }) =>
        row.original.picture ? (
          <img
            className={'w-9 h-9 rounded-lg object-cover object-center'}
            src={row.original.picture?.path}
            alt={'staff'}
          />
        ) : (
          '-'
        )
    },
    {
      version: ['FULL', 'PARTIAL', 'LIGHT'],
      enableSorting: true,
      accessorKey: 'lastName',
      header: 'NOM'
    },
    {
      version: ['FULL', 'PARTIAL', 'LIGHT'],
      enableSorting: true,
      accessorKey: 'firstName',
      header: 'Prénom'
    },
    {
      version: ['FULL', 'PARTIAL'],
      enableSorting: true,
      accessorKey: 'position',
      header: 'Poste',
      cell: ({ row }) => t(staffPositionEnumMapping[row.original.position])
    },
    {
      version: ['FULL', 'PARTIAL', 'LIGHT'],
      accessorKey: 'qualifications',
      header: 'Qualifications',
      cell: ({ row }) => <QualificationTags qualifications={row.original.qualifications} />
    },
    {
      version: ['FULL', 'PARTIAL'],
      accessorKey: 'languages',
      header: 'Langues',
      cell: ({ row }) =>
        row.original.languages.length === 0 ||
        row.original.languages.every(({ level }) => level === LanguageLevelEnum.NOT_PRACTICED) ? (
          '-'
        ) : (
          <div className={'flex flex-row items-center gap-1'}>
            {row.original?.languages
              ?.sort((a, b) => {
                const labelA = languageMapping[a.language].label
                const labelB = languageMapping[b.language].label
                return labelA < labelB ? -1 : labelA > labelB ? 1 : 0
              })
              .filter(({ level }) => level !== LanguageLevelEnum.NOT_PRACTICED)
              .map(({ language }) => {
                const Icon = languageMapping[language].icon
                return <Icon key={language} className={'size-6 rounded-full'} />
              })}
          </div>
        )
    },
    {
      version: ['FULL', 'PARTIAL', 'LIGHT'],
      enableSorting: true,
      accessorKey: 'birthDate',
      header: 'Âge',
      sortDescFirst: true,
      invertSorting: true,
      cell: ({ row }) => DateFormatter.calculateAge(row.original.birthDate)
    },
    {
      version: ['FULL', 'PARTIAL', 'LIGHT'],
      enableSorting: true,
      accessorKey: 'gender',
      header: 'Sexe',
      cell: ({ row }) => genderMapping[toGenderEnumValue(row.original.gender)]
    },
    {
      version: ['FULL'],
      enableSorting: true,
      accessorKey: 'postalCode',
      header: 'Dpt',
      cell: ({ row }) => row.original.postalCode?.slice(0, 2) ?? '-'
    },
    {
      version: ['FULL'],
      accessorKey: 'academicZone',
      header: 'Zone',
      enableSorting: true,
      cell: ({ row }) =>
        row.original.academicZone ? academicZoneEnumMapping[row.original.academicZone] : '-'
    },
    {
      version: ['FULL', 'PARTIAL'],
      enableSorting: true,
      accessorKey: 'missionsCount',
      header: 'Ancienneté',
      cell: ({ row }) =>
        valueRender(row.original.missionsCount, (missionsCount) => missionsCount.toString())
    },
    {
      version: ['FULL'],
      accessorKey: 'currentYearStays',
      header: 'Séjours',
      cell: ({ row }) =>
        !row.original.currentYearStays?.length ? (
          '-'
        ) : (
          <div className={'flex flex-col gap-1 py-3'}>
            {row.original.currentYearStays.map((stay, index) => (
              <div className={'flex flex-row items-center gap-2 whitespace-nowrap'} key={index}>
                {stay.countryCode && <CountryFlag code={stay.countryCode} />}
                <span>{`${DateFormatter.DDMM(stay.startDate)}-${DateFormatter.DDMM(stay.endDate)}`}</span>
                <span>{stay.formulaCodes.join(', ')}</span>
              </div>
            ))}
          </div>
        )
    },
    {
      version: ['FULL'],
      accessorKey: 'availabilities',
      header: 'Disponibilités',
      cell: ({ row }) => (
        <div className={'flex flex-col gap-1 py-3'}>
          {row.original.availabilities.length === 0
            ? '-'
            : row.original.availabilities.map((availability, index) => (
                <div className={'flex flex-row gap-1'} key={index}>
                  <TableDate date={availability.startDate} variant={'secondaryBlue'} />
                  <ArrowRightOneIcon color={'#D3D3D3'} height={'21px'} />
                  <TableDate date={availability.endDate} variant={'secondaryBlue'} />
                </div>
              ))}
        </div>
      )
    },
    {
      version: ['FULL'],
      accessorKey: 'user.status',
      header: 'Espace encadrant',
      cell: ({ row }) => <StaffAccountStatusBadge account={row.original.user} />
    },
    {
      version: ['FULL'],
      enableSorting: true,
      accessorKey: 'updatedAt',
      header: 'Màj',
      cell: ({ row }) => (
        <Badge text={DateFormatter.DDMMYYYY(row.original.updatedAt)} variant={'grey'} />
      )
    }
  ]
}

export const getStaffColumns = (
  version: StaffVersion,
  t: TFunction<AvailableLanguages, undefined>
): Column<Staff>[] => {
  return staffColumns(t).filter((column) => column.version.includes(version))
}

export const staffRequestColumns = (periods: PeriodTypes.Light[]): Column<StaffRequest>[] => [
  {
    accessorKey: 'id',
    header: '#'
  },
  {
    accessorKey: 'status',
    header: 'Statut',
    enableSorting: true,
    cell: ({ row }) => {
      const status = row.original.status
      return (
        <Badge
          variant={missionStatusEnumMapping[status].variant}
          text={missionStatusEnumMapping[status].label}
        />
      )
    }
  },
  {
    accessorKey: 'country.name',
    header: 'Pays',
    enableSorting: true,
    cell: ({ row }) => {
      // first try to get the country directly from the request
      if (row.original.country?.code) return <CountryFlag code={row.original.country.code} />

      // if no country is found get the first center country (since all centers have the same country in a request)
      const country = row.original.centers?.at(0)?.olJourneyPlace?.country
      if (!country) return '-'
      return <CountryFlag possibleCountry={country} />
    }
  },
  {
    accessorKey: 'centers.name',
    header: 'Centres',
    cell: ({ row }) => {
      const centers = Array.from(new Set(row.original.centers?.map((c) => c.name))).join(', ')
      return (
        <div className="w-32 whitespace-normal break-words py-1.5">
          {centers || row.original.oldCenter || '-'}
        </div>
      )
    }
  },
  {
    accessorKey: 'formulas.title',
    header: 'Formules',
    cell: ({ row }) => {
      const formulas = Array.from(
        new Set(row.original.formulas?.map((f) => getTranslatedName(f)))
      ).join(', ')
      return <div className="w-32 whitespace-normal break-words py-1.5">{formulas || '-'}</div>
    }
  },
  {
    accessorKey: 'centers.centerToJourneys.journey.mainOption.title',
    header: 'Options',
    cell: ({ row }) => {
      const options = Array.from(
        new Set(
          (row.original.centers ?? [])
            .flatMap((c) =>
              (c.centerToJourneys ?? []).map((d) => getTranslatedName(d?.journey?.mainOption))
            )
            .filter(Boolean)
        )
      ).join(', ')
      return (
        <div className="w-32 whitespace-normal break-words py-1.5">
          {options || row.original.oldMainOption || '-'}
        </div>
      )
    }
  },
  {
    accessorKey: 'startDate',
    header: 'Dates',
    cell: ({ row }) => {
      const startDate = row.original.startDate
      const endDate = row.original.endDate
      if (!startDate || !endDate) return '-'

      const requestStart = startOfDay(DateFormatter.toDate(startDate))
      const requestEnd = endOfDay(DateFormatter.toDate(endDate))

      const period = periods.find(({ startDate: ps, endDate: pe }) => {
        const periodStart = startOfDay(DateFormatter.toDate(ps))
        const periodEnd = endOfDay(DateFormatter.toDate(pe))

        // inclusive containment: periodStart <= requestStart && requestEnd <= periodEnd
        const startsOk = !isAfter(periodStart, requestStart)
        const endsOk = !isBefore(periodEnd, requestEnd)

        return startsOk && endsOk
      })

      return (
        <PeriodBadge
          period={period}
          text={`${DateFormatter.DDMMYYYY(startDate)} - ${DateFormatter.DDMMYYYY(endDate)}`}
        />
      )
    }
  },
  {
    accessorKey: 'duration',
    header: 'Durée (jours)',
    enableSorting: true,
    cell: ({ row }) => valueRender(row.original.duration)
  },
  {
    accessorKey: 'qualification',
    header: 'Qualification',
    enableSorting: true,
    cell: ({ row }) => <QualificationBadge qualification={row.original.qualification} />
  },
  {
    accessorKey: 'numberOfStaff',
    header: '# Demandés',
    enableSorting: true
  },
  {
    accessorKey: 'affectedStaffs.length',
    header: '# Affectés',
    enableSorting: true
  },
  {
    accessorKey: 'affectedStaffs.staff.firstName',
    header: 'Noms des encadrants affectés',
    cell: ({ row }) => {
      const affectedStaffs = row.original.affectedStaffs
        .map((affectedStaff) =>
          toFullName(affectedStaff.staff.firstName, affectedStaff.staff.lastName, {
            options: { mode: 'lastname-firstname' }
          })
        )
        .join(', ')
      if (!affectedStaffs) return '-'
      return <div className="text-wrap w-32">{affectedStaffs}</div>
    }
  }
]

export const getStaffNameLabel = (firstName: string, lastName: string, phone: string) => {
  return `${StringFormatter.capitalize(firstName)} ${lastName.toUpperCase()} (${phone})`
}
